import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'

import { useGetUnitPlans } from '@apps-common/apis/portal/unit'
import { QUERY_GET_UNIT_ACTIVATION_TEMPLATES, useGetUnitActivationTemplates } from '@apps-common/apis/portal/unit-activations'
import {
  TemplateCard,
  TemplateModifyModal
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/template-modify'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm, SelectInputSM } from '@apps-common/components/elements'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { motionGrowFadeIn } from '@apps-common/config/motion-variants'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IActivationServiceRequestTemplateModel } from '@apps-common/types/portal/service-request'

interface IProps {
  onSelected: (data: IActivationServiceRequestTemplateModel) => void
}

export const TemplateModify: React.FC<IProps> = ({ onSelected }) => {
  const [selected, setSelected] = useState<IActivationServiceRequestTemplateModel>()
  const { data, isLoading, isError } = useGetUnitActivationTemplates({})
  const { data: plans } = useGetUnitPlans({})

  const onChange = (id: string) => {
    const template = data?.find((t) => t.id?.toString() === id)

    if (template) {
      setSelected(template)
    } else {
      setSelected(undefined)
    }
  }

  const loading = isLoading || isError || !data

  return (
    <div className='bg-surface border border-border rounded-3xl p-6 grow flex flex-col gap-[20px] min-w-[360px]'>
      <div className='flex items-center justify-between gap-[10px]'>
        <SectionTitle text='Templates' />
        <ModalButton
          classes='px-[5px]'
          component={TemplateModifyModal}
          componentProps={{
            templateId: undefined
          }}
          confirmCallback={() => {
            queryClient.invalidateQueries({ queryKey: QUERY_GET_UNIT_ACTIVATION_TEMPLATES() })
          }}
          enterAnimation={modalSlideUpEnterAnimation}
          leaveAnimation={modalSlideUpLeaveAnimation}
        >
          <ButtonRoundSm type='div' className='action-success'>
            <commonIcons.add className='text-[1.25rem]' />
          </ButtonRoundSm>
        </ModalButton>
      </div>
      <div>
        <AnimatePresence mode='wait'>
          {!loading && (!data || data?.length === 0) && (
            <motion.div key='unit-activation-template-create-option' {...motionGrowFadeIn(0.2)} className='w-full'>
              <div className='bg-primary-content/10 p-6 rounded-3xl'>
                <div className='flex items-center justify-between gap-[20px] w-full'>
                  <div className='grow jost text-[0.9em]'>No templates. Create a new template to start activating using templates.</div>
                  <ModalButton
                    classes='bg-primary-content/10 text-primary-content rounded-2xl p-6'
                    component={TemplateModifyModal}
                    componentProps={{
                      templateId: undefined
                    }}
                    confirmCallback={() => {
                      queryClient.invalidateQueries({ queryKey: QUERY_GET_UNIT_ACTIVATION_TEMPLATES() })
                    }}
                    enterAnimation={modalSlideUpEnterAnimation}
                    leaveAnimation={modalSlideUpLeaveAnimation}
                  >
                    <commonIcons.add className='text-[1.25rem]' />
                  </ModalButton>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <SelectInputSM
          type='text'
          placeholder='Select activation template'
          label='Activation template'
          id='activation-templates'
          containerClassName='!px-0'
          className='capitalize'
          showLabel
          loading={loading && data && data?.length > 0}
          disabled={loading}
          registration={{
            value: selected?.id?.toString(),
            onChange: (e: any) => onChange(e.target.value)
          }}
          options={data?.map((t) => ({ label: t.name, value: t.id.toString() })) || []}
        />
        <div className='text-[0.8em] text-text-muted/70 px-2'>
          Activate a device using a predefined template. You can create a new template to start activating using templates.
        </div>
        <VertSpacerMd />
        <AnimatePresence mode='wait'>
          {!loading && selected && (
            <motion.div key={`unit-activation-template-selection-card-${selected.id}`} {...motionGrowFadeIn(0.2)} className='w-full mt-4'>
              <TemplateCard
                template={selected}
                plans={plans}
                onSelected={(d) => onSelected(d)}
                clearSelected={() => setSelected(undefined)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
